import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { yupResolver } from '@hookform/resolvers/yup';
import { SelectChangeEvent } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ONE_PAGE_LINK_STATUS } from 'constants/businessPlan';
import { businessPlanQueryKeys } from 'services/businessPlan/businessPlan-qk';
import { BusinessPlanApi } from 'services/businessPlan/businessPlan.api';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IOpenLinkStatus } from 'interfaces/businessPlan';

import { BlackToast } from 'components/_basic/BlackToast/BlackToast';
import { Responsive } from 'components/_basic/Responsive';
import { IDropdownOptions } from 'components/ChangeDocAccessSelect/types';
import { IAccountPersonalInfoForm } from 'components/ShareLinksForm/types';

import ToastService from 'helpers/ToastService';
import { grantAccessValidationSchema } from 'helpers/validations/grantAccessValidation';
import { copyOnePageLink } from 'helpers/viewDocHandler';

import { ManageShareMobile } from '../ManageShareMobile/ManageShareMobile';
import { ManageShareModal } from '../ManageShareModal';

interface IProps {
  isOpened: boolean;
  id: string;
  onClose: VoidFunction;
  // onCopyLink: VoidFunction;
  openLinkStatus?: IOpenLinkStatus;
}

const getActionFromStatus = (status: IOpenLinkStatus) => {
  const shareStatusToActionMap = new Map([
    [ONE_PAGE_LINK_STATUS.RESTRICTED, 'close' as const],
    [ONE_PAGE_LINK_STATUS.PUBLIC, 'open' as const],
  ]);

  return shareStatusToActionMap.get(status) ?? ('close' as const);
};

export const ManageOnePageShare: FC<IProps> = ({
  isOpened,
  onClose,
  id,
  openLinkStatus = ONE_PAGE_LINK_STATUS.RESTRICTED,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [dropdownValue, setDropdownValue] = useState<IOpenLinkStatus>(openLinkStatus);

  const { track, isPending } = usePromiseStatus();

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
    reset,
  } = useForm<IAccountPersonalInfoForm>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver<IAccountPersonalInfoForm>(grantAccessValidationSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const isRestricted = dropdownValue === ONE_PAGE_LINK_STATUS.RESTRICTED;

  const dropdownOptions: IDropdownOptions[] = [
    {
      value: ONE_PAGE_LINK_STATUS.RESTRICTED,
      title: t('restrictedAccess'),
      text: t('onlyYouCanAccess'),
    },
    {
      value: ONE_PAGE_LINK_STATUS.PUBLIC,
      title: t('anyoneWithTheLink'),
      text: t('anyoneCanView'),
    },
  ];

  const submitHandler = async ({ email }: IAccountPersonalInfoForm) => {
    try {
      await track(BusinessPlanApi.shareOnePageLinkByMail({ id, email }));

      toast(<BlackToast text="Viewer has been invited" />, {
        position: 'top-center',
      });
      reset();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);

        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to share business plan:', {
            errorType: error instanceof Error ? error.name : typeof error,
            message: 'Failed to send invitation',
          });
        }
      }
    }
  };

  const copyLinkClickHandler = () => {
    copyOnePageLink(id);
    setIsLinkCopied(true);
  };

  const dropdownChangeHandler = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as IOpenLinkStatus;
    try {
      setDropdownValue(value);

      await track(BusinessPlanApi.changeOnePagePublicStatus({ id, action: getActionFromStatus(value) }));

      queryClient.invalidateQueries({ queryKey: businessPlanQueryKeys.businessPlanPublicStatus(id) });
    } catch (error) {
      error instanceof Error && ToastService.showError('Try again later', 'Something went wrong');
    }
  };

  const props = {
    isRestricted,
    formButtonText: 'Share',
    formLabel: 'Invite viewers',
    register,
    isFormButtonDisabled: !isValid || isPending,
    isRequestPending: isPending,
    formError: errors.email?.message,
    dropdownValue,
    dropdownOptions,
    onDropdownChange: dropdownChangeHandler,
    onCopyLinkClick: copyLinkClickHandler,
    onFormSubmit: handleSubmit(submitHandler),
    isLinkCopied,
  };

  return (
    <>
      <Responsive mobile>
        <ManageShareMobile isOpened={isOpened} onClose={onClose} {...props} />
      </Responsive>
      <Responsive aboveMobile>
        <ManageShareModal onModalClose={onClose} {...props} />
      </Responsive>
    </>
  );
};
